import { Button, Button as ShadcnButton } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/components/ui/use-toast'
import { useUserStore } from '@/stores/user'
import {
  Alert,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast as sonnerToast } from 'sonner'
import { Modal } from '../../../components'
import { useInputText } from '../../../hooks'
import services from '../../../services'
import { ModalUpdateProps } from './@types'
import { useData } from './use-data'

export default function ModalUpdate({ open }: ModalUpdateProps) {
  const { clearUser, user } = useUserStore()
  const { users, refetch } = useData()
  const initialValues = { name: '', surname: '', email: '', password: '' }
  const input = useInputText(initialValues)

  const [level, setLevel] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [originalEmail, setOriginalEmail] = useState('')
  const { toast, dismiss } = useToast()

  const onLevelChange = (event: SelectChangeEvent) =>
    setLevel(Number(event.target.value))

  const closeModal = () => {
    input.clear()
    setLevel(1)
    setError(null)
    refetch()
    clearUser()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)

      const { email, password } = input

      if (email !== originalEmail) {
        toast({
          title: 'Confirmar alteração de e-mail',
          description:
            'Tem a certeza de que pretende alterar o e-mail deste utilizador?',
          action: (
            <div className="flex gap-2">
              <ShadcnButton
                onClick={confirmCriticalChanges}
                variant="default"
                size="sm"
              >
                Sim
              </ShadcnButton>
              <ShadcnButton
                onClick={() => dismiss()}
                variant="secondary"
                size="sm"
              >
                Não
              </ShadcnButton>
            </div>
          ),
          duration: 999999999,
        })
        return
      }

      if (password) {
        toast({
          title: 'Confirmar alteração de password',
          description:
            'Tem a certeza de que pretende alterar a password deste utilizador?',
          action: (
            <div className="flex gap-2">
              <ShadcnButton
                onClick={confirmCriticalChanges}
                variant="default"
                size="sm"
              >
                Sim
              </ShadcnButton>
              <ShadcnButton
                onClick={() => dismiss()}
                variant="secondary"
                size="sm"
              >
                Não
              </ShadcnButton>
            </div>
          ),
          duration: 999999999,
        })
        return
      }

      await updateUser()
    } catch (error) {
      setError((error as Error).message)
    }
  }

  const confirmCriticalChanges = async () => {
    dismiss()
    await updateUser()
  }

  const updateUser = async () => {
    try {
      setLoading(true)
      const { name, surname, email, password } = input

      await services.user.update({
        id: user?.id,
        level,
        name,
        surname,
        email,
        newPassword: password,
      })

      sonnerToast.success('Utilizador atualizado com sucesso.')
      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      user.name && input.updateInitial('name', user.name)
      user.surname && input.updateInitial('surname', user.surname)
      user.level && setLevel(user.level)
      if (user.email) {
        input.updateInitial('email', user.email)
        setOriginalEmail(user.email)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, users])

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Atualizar Utilizador</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-12">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <ScrollArea className="h-[380px] w-full">
              <div className="grid gap-8 my-2">
                <TextField
                  autoFocus
                  label="Primeiro nome"
                  name="name"
                  value={input.name}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.name}
                  helperText={input.errors.name}
                  disabled={loading}
                />

                <TextField
                  label="Apelido"
                  name="surname"
                  value={input.surname}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.surname}
                  helperText={input.errors.surname}
                  disabled={loading}
                />

                <TextField
                  label="E-mail"
                  name="email"
                  type="email"
                  value={input.email}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.email}
                  helperText={input.errors.email}
                  disabled={loading}
                />

                <TextField
                  label="Nova Password (opcional)"
                  name="password"
                  type="password"
                  value={input.password}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.password}
                  helperText={input.errors.password}
                  disabled={loading}
                />

                <FormControl fullWidth>
                  <InputLabel id="select-level" className="!text-white">
                    Nível
                  </InputLabel>
                  <Select
                    labelId="select-level"
                    label="nível"
                    value={String(level)}
                    onChange={onLevelChange}
                    disabled={loading}
                  >
                    {[1, 2, 3, 4, 5].map((index) => (
                      <MenuItem disabled={loading} key={index} value={index}>
                        {String(index)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </ScrollArea>

            <div className="w-max max-w-full ml-auto mt-2 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="reset"
                onClick={closeModal}
              >
                Cancelar
              </Button>

              <Button disabled={loading} type="submit">
                Atualizar
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
